import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import {
  MicOutlined as MicIcon,
  FavoriteBorderOutlined as FavoriteIcon,
  FolderOutlined as FolderIcon,
} from '@mui/icons-material';
import { AppColors } from '../theme/colors';
import { useAuth, useCompany } from '../hooks/useAuth';
import { getBoxTemplate } from '../constants/industryTemplates';

interface AdminBottomNavigationProps {
  currentTab?: 'voice' | 'thanks' | 'theme';
  onTabChange?: (tab: 'voice' | 'thanks' | 'theme') => void;
}

export const AdminBottomNavigation: React.FC<AdminBottomNavigationProps> = ({
  currentTab,
  onTabChange
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { company } = useCompany(user?.uid || null);

  // 現在のタブを判定
  const getCurrentTab = (): 'voice' | 'thanks' | 'theme' | null => {
    if (currentTab) return currentTab;
    
    if (location.pathname.includes('/admin/theme-posts') || location.pathname.includes('theme')) {
      return 'theme';
    }
    if (location.pathname.includes('thanks')) {
      return 'thanks';
    }
    if (location.pathname.includes('voice') || location.pathname === '/admin') {
      return 'voice';
    }
    return null;
  };

  const activeTab = getCurrentTab();

  const handleTabClick = (tab: 'voice' | 'thanks' | 'theme') => {
    // onTabChangeが提供されている場合は、それを呼び出す（AdminPageなど）
    if (onTabChange) {
      onTabChange(tab);
      return;
    }
    
    // onTabChangeが提供されていない場合は、ページ遷移する（ThemePostsPageなど）
    if (tab === 'voice') {
      navigate('/admin?tab=voice');
    } else if (tab === 'thanks') {
      navigate('/admin?tab=thanks');
    } else if (tab === 'theme') {
      navigate('/admin/theme-posts');
    }
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        height: 80,
        bgcolor: 'white',
        borderTop: `1px solid ${AppColors.border}`,
        boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.1)',
        zIndex: 1000,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          height: '100%',
          alignItems: 'center',
        }}
      >
        {/* アイデアボックス */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            height: '100%',
            bgcolor: activeTab === 'voice' ? AppColors.primary + '20' : 'transparent',
            transition: 'background-color 0.2s',
          }}
          onClick={() => handleTabClick('voice')}
        >
          <MicIcon
            sx={{
              color: activeTab === 'voice' ? AppColors.primary : AppColors.textSecondary,
              mb: 0.5,
              fontSize: 24,
            }}
          />
          <Typography
            sx={{
              fontSize: 12,
              color: activeTab === 'voice' ? AppColors.primary : AppColors.textSecondary,
              fontWeight: activeTab === 'voice' ? 600 : 400,
            }}
          >
            {getBoxTemplate(company?.industryType, 'voice').name}
          </Typography>
        </Box>

        {/* サンクスボックス */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            height: '100%',
            bgcolor: activeTab === 'thanks' ? AppColors.primary + '20' : 'transparent',
            transition: 'background-color 0.2s',
          }}
          onClick={() => handleTabClick('thanks')}
        >
          <FavoriteIcon
            sx={{
              color: activeTab === 'thanks' ? AppColors.primary : AppColors.textSecondary,
              mb: 0.5,
              fontSize: 24,
            }}
          />
          <Typography
            sx={{
              fontSize: 12,
              color: activeTab === 'thanks' ? AppColors.primary : AppColors.textSecondary,
              fontWeight: activeTab === 'thanks' ? 600 : 400,
            }}
          >
            {getBoxTemplate(company?.industryType, 'thanks').name}
          </Typography>
        </Box>

        {/* テーマボックス */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            height: '100%',
            bgcolor: activeTab === 'theme' ? AppColors.primary + '20' : 'transparent',
            transition: 'background-color 0.2s',
          }}
          onClick={() => handleTabClick('theme')}
        >
          <FolderIcon
            sx={{
              color: activeTab === 'theme' ? AppColors.primary : AppColors.textSecondary,
              mb: 0.5,
              fontSize: 24,
            }}
          />
          <Typography
            sx={{
              fontSize: 12,
              color: activeTab === 'theme' ? AppColors.primary : AppColors.textSecondary,
              fontWeight: activeTab === 'theme' ? 600 : 400,
            }}
          >
            {getBoxTemplate(company?.industryType, 'theme').name}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

